<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  getOutPorts,
  getInPorts,
  nodeHasHold,
} from '../../composables/useCsmlPorts';
import {
  fileNameOf,
  getNodeIssues,
  getNodeSummary,
} from '../../composables/useCsmlNodeMeta';
import { nodeTemplates } from '../../constants/csmlNodeTemplates';
import {
  getCategoryStyle,
  getPortIcon,
} from '../../constants/csmlNodeCategories';

const props = defineProps({
  node: { type: Object, required: true },
  selected: { type: Boolean, default: false },
  selectionSize: { type: Number, default: 0 },
  hasIncoming: { type: Boolean, default: false },
  zoom: { type: Number, default: 1 },
  connectingFromId: { type: [Number, String], default: null },
  connectedPorts: { type: Array, default: () => [] },
  isPanMode: { type: Boolean, default: false },
});

const emit = defineEmits([
  'select',
  'portClick',
  'dragMove',
  'dragEnd',
  'delete',
  'duplicate',
  'addNext',
  'addAtPort',
  'portsMeasured',
]);

const { t, te } = useI18n();

const outPorts = computed(() => getOutPorts(props.node));
const inPorts = computed(() => getInPorts(props.node));
const hasHold = computed(() => nodeHasHold(props.node));
const isConnectingTarget = computed(
  () =>
    props.connectingFromId != null && props.connectingFromId !== props.node.id
);

const style = computed(() => getCategoryStyle(props.node.type));

// One treatment per state, in priority order, instead of stacked rings.
const stateClass = computed(() => {
  if (isConnectingTarget.value)
    return 'border-n-teal-9 ring-2 ring-n-teal-9/40';
  if (props.selected) return 'border-n-brand ring-2 ring-n-brand/30';
  return 'border-n-weak hover:border-n-slate-7';
});
const icon = computed(
  () => nodeTemplates[props.node.type]?.icon || 'i-lucide-box'
);
const isStart = computed(() => props.node.type === 'start');

// The start node is the entry point, so it has nothing arriving at it - its
// in-port only appears when a wire already loops back to it, or while one is
// being drawn.
const showInPort = computed(
  () => !isStart.value || props.hasIncoming || isConnectingTarget.value
);

// The node's persisted `label` comes from the template (or from whatever the
// script importer guessed), so prefer the translated name for the type and
// fall back to the stored one only when there is no key for it.
const typeLabel = computed(() => {
  const key = `AGENT_BOTS.VISUAL_BUILDER.NODES.${String(
    props.node.type
  ).toUpperCase()}.LABEL`;
  return te(key) ? t(key) : props.node.label;
});

const onAddAtPort = (port, event) => {
  emit('addAtPort', {
    nodeId: props.node.id,
    portIdx: port.idx,
    clientX: event.clientX,
    clientY: event.clientY,
  });
};

const portLabel = port =>
  port.labelKey
    ? t(`AGENT_BOTS.VISUAL_BUILDER.PORTS.${port.labelKey}`)
    : port.label;

const summary = computed(() => getNodeSummary(props.node));
const issues = computed(() => getNodeIssues(props.node));
const issueSummary = computed(() =>
  issues.value
    .map(issue => t(`AGENT_BOTS.VISUAL_BUILDER.ISSUES.${issue}`))
    .join('\n')
);

// Branch rows are only worth the vertical space when the node actually forks.
// A single-port node keeps one dot centred on its right edge instead.
const branchPorts = computed(() =>
  outPorts.value.length > 1 ? outPorts.value : []
);
const isPortConnected = port => props.connectedPorts.includes(port.idx);

// A wired port is a solid dot in the category colour; one that still leads
// nowhere is a dashed, empty ring.
const outPortClass = connected => [
  connected
    ? [style.value.port, style.value.portDot]
    : 'border-dashed border-n-slate-8 bg-n-solid-1',
  props.connectingFromId === props.node.id ? 'ring-2 ring-n-brand' : '',
];

// A thumbnail that fails to load (dead link, not an image) keeps its frame
// and shows a placeholder, so the card does not jump in height.
const thumbnailFailed = ref(false);
watch(
  () => summary.value.thumbnail,
  () => {
    thumbnailFailed.value = false;
  }
);

const MEDIA_ICONS = {
  video: 'i-lucide-video',
  audio: 'i-lucide-audio-lines',
  file: 'i-lucide-file-text',
};

// File, video and audio have no preview to draw, so they get a typed tile
// carrying the document name instead.
const mediaTile = computed(() => {
  const mediaIcon = MEDIA_ICONS[props.node.type];
  if (!mediaIcon || !props.node.content) return null;
  return {
    icon: mediaIcon,
    name: fileNameOf(props.node.content) || summary.value.text,
  };
});

let dragOrigin = null;
const isDragging = ref(false);

const onMouseDown = event => {
  if (event.button !== 0) return;
  if (event.target.closest('[data-port]')) return;
  if (event.target.closest('[data-node-action]')) return;

  // While connecting, clicking the node body completes to its in-port
  if (props.connectingFromId != null) {
    event.stopPropagation();
    emit('portClick', {
      nodeId: props.node.id,
      side: 'in',
      portIdx: 0,
    });
    return;
  }

  // Leave the event bubbling so the canvas can pan from the node body.
  if (props.isPanMode) return;

  // Shift toggles the node in the selection. Pressing a node that is already
  // part of a group keeps the group so it can be dragged as one; only a plain
  // click (no movement) narrows it down to this node.
  if (event.shiftKey) {
    emit('select', { id: props.node.id, additive: true });
    return;
  }
  event.stopPropagation();
  const keepGroup = props.selected && props.selectionSize > 1;
  if (!keepGroup) emit('select', { id: props.node.id });
  let moved = false;
  dragOrigin = {
    startX: event.clientX,
    startY: event.clientY,
    nodeX: props.node.x,
    nodeY: props.node.y,
  };
  const onMove = e => {
    if (!dragOrigin) return;
    const dx = (e.clientX - dragOrigin.startX) / props.zoom;
    const dy = (e.clientY - dragOrigin.startY) / props.zoom;
    if (dx || dy) {
      moved = true;
      isDragging.value = true;
    }
    emit('dragMove', {
      id: props.node.id,
      x: dragOrigin.nodeX + dx,
      y: dragOrigin.nodeY + dy,
      free: e.altKey,
    });
  };
  const onUp = () => {
    dragOrigin = null;
    isDragging.value = false;
    if (keepGroup && !moved) emit('select', { id: props.node.id });
    emit('dragEnd');
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  };
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
};

const onPortPointerDown = (side, portIdx, event) => {
  event.preventDefault();
  event.stopPropagation();
  emit('portClick', {
    nodeId: props.node.id,
    side,
    portIdx,
    clientX: event.clientX,
    clientY: event.clientY,
  });
};

// Ports sit inside normal flow now (one per branch row), so their real
// on-screen offset is measured from the rendered DOM rather than recomputed
// by a pixel formula elsewhere - that duplication is what used to let
// connector lines drift out of sync with the actual port dots. Measuring via
// getBoundingClientRect relative to the node's own rect - rather than
// offsetTop/offsetLeft - correctly accounts for any CSS transform applied to
// a port; dividing by the current zoom converts the on-screen gap back to
// the same unscaled content units node.x/node.y are expressed in, so the
// result stays valid at any zoom level.
const rootRef = ref(null);
let resizeObserver = null;

const measurePorts = () => {
  if (!rootRef.value) return;
  const nodeRect = rootRef.value.getBoundingClientRect();
  const scale = props.zoom || 1;
  const measured = { in: [], out: [] };
  rootRef.value.querySelectorAll('[data-port]').forEach(el => {
    const side = el.dataset.side;
    if (!measured[side]) return;
    const r = el.getBoundingClientRect();
    measured[side].push({
      idx: Number(el.dataset.portIdx),
      x: (r.left + r.width / 2 - nodeRect.left) / scale,
      y: (r.top + r.height / 2 - nodeRect.top) / scale,
    });
  });
  emit('portsMeasured', {
    nodeId: props.node.id,
    height: nodeRect.height / scale,
    ...measured,
  });
};

onMounted(() => {
  measurePorts();
  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(measurePorts);
    resizeObserver.observe(rootRef.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});
</script>

<template>
  <div
    ref="rootRef"
    class="absolute z-10 w-64 border select-none group bg-n-solid-1 transition-[box-shadow,transform] duration-150 ease-out motion-reduce:transition-none"
    :class="[
      stateClass,
      isStart ? 'rounded-3xl' : 'rounded-xl',
      isDragging
        ? 'z-20 cursor-grabbing scale-[1.02] shadow-lg ring-2 ring-n-brand/20'
        : 'cursor-grab shadow-sm hover:shadow-md',
    ]"
    :style="{ left: `${node.x}px`, top: `${node.y}px` }"
    data-node-root
    :data-node-id="node.id"
    @mousedown="onMouseDown"
  >
    <div
      class="absolute z-30 pb-1.5 transition-opacity opacity-0 pointer-events-none bottom-full end-0 group-hover:pointer-events-auto group-hover:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100"
      :class="{ 'opacity-100 pointer-events-auto': selected }"
    >
      <div
        class="flex items-center gap-0.5 p-0.5 border rounded-lg shadow-sm border-n-weak bg-n-solid-1"
      >
        <button
          v-if="outPorts.length"
          type="button"
          data-node-action
          class="p-1.5 rounded-md hover:bg-n-alpha-2 text-n-slate-11"
          :title="t('AGENT_BOTS.VISUAL_BUILDER.ADD_NEXT_NODE')"
          @click.stop="emit('addNext', node.id)"
        >
          <Icon icon="i-lucide-plus" class="size-3.5" />
        </button>
        <template v-if="!isStart">
          <button
            type="button"
            data-node-action
            class="p-1.5 rounded-md hover:bg-n-alpha-2 text-n-slate-11"
            :title="t('AGENT_BOTS.VISUAL_BUILDER.DUPLICATE_NODE')"
            @click.stop="emit('duplicate', node.id)"
          >
            <Icon icon="i-lucide-copy" class="size-3.5" />
          </button>
          <button
            type="button"
            data-node-action
            class="p-1.5 rounded-md hover:bg-n-ruby-3 text-n-ruby-11"
            :title="t('AGENT_BOTS.VISUAL_BUILDER.DELETE_NODE')"
            @click.stop="emit('delete', node.id)"
          >
            <Icon icon="i-lucide-trash-2" class="size-3.5" />
          </button>
        </template>
      </div>
    </div>

    <span
      v-if="issues.length"
      class="absolute z-30 flex items-center justify-center rounded-full -top-2 -end-2 size-5 bg-n-ruby-9 text-[10px] font-bold text-white shadow-sm border-2 border-n-solid-1"
      :title="issueSummary"
    >
      {{ issues.length }}
    </span>
    <span
      class="absolute w-1 rounded-e-full left-0 inset-y-3"
      :class="style.accent"
    />

    <p
      v-if="isStart"
      class="px-4 pt-2.5 text-[10px] font-semibold tracking-wide uppercase text-n-teal-11"
    >
      {{ t('AGENT_BOTS.VISUAL_BUILDER.CONVERSATION_STARTS') }}
    </p>

    <div
      class="flex items-center gap-2.5 ps-4 pe-3"
      :class="isStart ? 'pb-2.5 pt-1.5' : 'py-2.5'"
    >
      <span
        class="relative flex items-center justify-center rounded-lg size-7 shrink-0"
        :class="style.chip"
        :title="`#${node.id}`"
      >
        <Icon :icon="icon" class="size-3.5" />
      </span>
      <p class="flex-1 min-w-0 text-xs font-semibold truncate text-n-slate-12">
        {{ typeLabel }}
      </p>
    </div>

    <div
      v-if="hasHold || summary.thumbnail || summary.text || summary.tags.length"
      class="flex flex-col gap-1.5 pb-2.5 ps-4 pe-3"
    >
      <div
        v-if="summary.thumbnail"
        class="flex items-center justify-center w-full overflow-hidden rounded-md aspect-video bg-n-alpha-2"
      >
        <img
          v-if="!thumbnailFailed"
          :src="summary.thumbnail"
          alt=""
          class="object-cover size-full"
          @error="thumbnailFailed = true"
        />
        <Icon v-else icon="i-lucide-image-off" class="size-6 text-n-slate-9" />
      </div>
      <div
        v-if="mediaTile"
        class="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-n-alpha-2"
      >
        <Icon :icon="mediaTile.icon" class="size-5 shrink-0 text-n-slate-11" />
        <span class="min-w-0 text-[11px] font-medium truncate text-n-slate-12">
          {{ mediaTile.name }}
        </span>
      </div>
      <p
        v-if="summary.text && !mediaTile"
        class="text-[11px] leading-snug text-n-slate-11 line-clamp-2 break-words"
      >
        {{ summary.text }}
      </p>
      <span
        v-if="hasHold"
        class="flex items-center self-start gap-1 rounded-full bg-n-amber-3 px-1.5 py-0.5 text-[10px] font-medium text-n-amber-11"
      >
        <Icon icon="i-lucide-hourglass" class="size-3" />
        {{ t('AGENT_BOTS.VISUAL_BUILDER.WAITS_FOR_REPLY') }}
      </span>
      <div v-if="summary.tags.length" class="flex flex-wrap gap-1">
        <span
          v-for="tag in summary.tags"
          :key="tag"
          class="rounded bg-n-alpha-2 px-1 py-px text-[9px] font-medium text-n-slate-11"
        >
          {{ tag }}
        </span>
      </div>
    </div>

    <ul v-if="branchPorts.length" class="border-t border-n-weak">
      <li
        v-for="port in branchPorts"
        :key="`branch-${port.idx}`"
        class="relative flex items-center h-8 gap-2 pl-4 pr-5 border-b border-n-weak last:border-b-0"
      >
        <Icon
          :icon="getPortIcon(port.type)"
          class="size-3 shrink-0"
          :class="isPortConnected(port) ? 'text-n-slate-11' : 'text-n-slate-9'"
        />
        <span
          class="flex-1 min-w-0 text-[11px] font-medium truncate"
          :class="isPortConnected(port) ? 'text-n-slate-12' : 'text-n-slate-10'"
          :title="portLabel(port)"
        >
          {{ portLabel(port) }}
        </span>
        <button
          v-if="!isPortConnected(port)"
          type="button"
          data-node-action
          class="absolute z-20 flex items-center justify-center -translate-y-1/2 border rounded-full opacity-0 -right-9 top-1/2 size-5 border-n-weak bg-n-solid-1 text-n-slate-11 hover:border-n-brand hover:bg-n-brand hover:text-white group-hover:opacity-100"
          :title="t('AGENT_BOTS.VISUAL_BUILDER.ADD_NODE_HERE')"
          @mousedown.stop
          @click.stop="onAddAtPort(port, $event)"
        >
          <Icon icon="i-lucide-plus" class="size-3" />
        </button>
        <button
          type="button"
          data-port
          data-side="out"
          :data-port-idx="port.idx"
          class="absolute z-20 flex items-center justify-center -translate-y-1/2 cursor-crosshair group/port -right-3 top-1/2 size-6"
          :title="portLabel(port)"
          @mousedown="onPortPointerDown('out', port.idx, $event)"
        >
          <span
            class="block transition-transform border-2 rounded-full size-3 group-hover/port:scale-150"
            :class="outPortClass(isPortConnected(port))"
          />
        </button>
      </li>
    </ul>

    <button
      v-for="port in showInPort ? inPorts : []"
      :key="`in-${port.idx}`"
      type="button"
      data-port
      data-side="in"
      :data-port-idx="port.idx"
      class="absolute z-20 flex items-center justify-center -translate-y-1/2 cursor-crosshair group/port -left-3 top-1/2 size-6"
      :class="{ 'opacity-40': connectingFromId === node.id }"
      :title="portLabel(port)"
      @mousedown="onPortPointerDown('in', port.idx, $event)"
    >
      <span
        class="block transition-transform border-2 rounded-full size-3 border-n-brand bg-n-brand group-hover/port:scale-150"
        :class="{ 'ring-4 ring-n-teal-9/40 animate-pulse': isConnectingTarget }"
      />
    </button>

    <template
      v-for="port in branchPorts.length ? [] : outPorts"
      :key="`add-${port.idx}`"
    >
      <button
        v-if="!isPortConnected(port)"
        type="button"
        data-node-action
        class="absolute z-20 flex items-center justify-center -translate-y-1/2 border rounded-full opacity-0 -right-9 top-1/2 size-5 border-n-weak bg-n-solid-1 text-n-slate-11 hover:border-n-brand hover:bg-n-brand hover:text-white group-hover:opacity-100"
        :title="t('AGENT_BOTS.VISUAL_BUILDER.ADD_NODE_HERE')"
        @mousedown.stop
        @click.stop="onAddAtPort(port, $event)"
      >
        <Icon icon="i-lucide-plus" class="size-3" />
      </button>
    </template>
    <button
      v-for="port in branchPorts.length ? [] : outPorts"
      :key="`out-${port.idx}`"
      type="button"
      data-port
      data-side="out"
      :data-port-idx="port.idx"
      class="absolute z-20 flex items-center justify-center -translate-y-1/2 cursor-crosshair group/port -right-3 top-1/2 size-6"
      :title="portLabel(port)"
      @mousedown="onPortPointerDown('out', port.idx, $event)"
    >
      <span
        class="block transition-transform border-2 rounded-full size-3 group-hover/port:scale-150"
        :class="outPortClass(isPortConnected(port))"
      />
    </button>
  </div>
</template>
